import dayjs, { type Dayjs } from 'dayjs';
import type { DashboardPeriod, DonemSecenegi } from '../PeriodTabs';

export interface Donem {
  bas: string;   // YYYY-MM-DD
  bit: string;   // YYYY-MM-DD
  tekGun: boolean;
}

/** Satis Analiz'in donemleri: Ozet'in bes donemi + "Bu hafta" (G2/K3). */
export type SatisDonemi = DashboardPeriod | 'hafta';

export const SATIS_DONEM_SECENEKLERI: ReadonlyArray<DonemSecenegi<SatisDonemi>> = [
  { key: 'gunluk', label: 'Günlük' },
  { key: 'dun', label: 'Dün' },
  { key: 'hafta', label: 'Bu hafta' },
  { key: 'aylik', label: 'Aylık' },
  { key: 'yillik', label: 'Yıllık' },
  { key: 'ozel', label: 'Özel' },
];

export function satisDonemiMi(x: string | null): x is SatisDonemi {
  return x !== null && SATIS_DONEM_SECENEKLERI.some((s) => s.key === x);
}

/**
 * Donem secimini rapor parametrelerine cevirir. 'ozel' burada YALNIZ ilk deger uretir; gercek aralik
 * URL'deki `bas`/`bit`ten gelir (bkz. useSatisAnalizFiltre). `bugun` test icin disaridan verilebilir.
 */
export function donemHesapla(p: SatisDonemi, bugun: Dayjs = dayjs()): Donem {
  switch (p) {
    case 'gunluk':
      return { bas: bugun.format('YYYY-MM-DD'), bit: bugun.format('YYYY-MM-DD'), tekGun: true };
    case 'dun': {
      const d = bugun.subtract(1, 'day');
      return { bas: d.format('YYYY-MM-DD'), bit: d.format('YYYY-MM-DD'), tekGun: true };
    }
    case 'hafta': {
      // dayjs().day(): 0 = Pazar. Pazartesiye geri sayim: (gun + 6) % 7. Locale eklentisi GEREKMEZ.
      const geri = (bugun.day() + 6) % 7;
      return {
        bas: bugun.subtract(geri, 'day').format('YYYY-MM-DD'),
        bit: bugun.format('YYYY-MM-DD'),
        tekGun: geri === 0,
      };
    }
    case 'yillik':
      return {
        bas: bugun.startOf('year').format('YYYY-MM-DD'),
        bit: bugun.format('YYYY-MM-DD'),
        tekGun: false,
      };
    case 'aylik':
    case 'ozel':
    default:
      return {
        bas: bugun.startOf('month').format('YYYY-MM-DD'),
        bit: bugun.format('YYYY-MM-DD'),
        tekGun: false,
      };
  }
}

/**
 * `donem` icin ekranda gosterilen, gercekten kullanici degerine sahip metin
 * (yalniz test icin var olan gizli bir alan DEGIL — PeriodTabs'in yanindaki
 * gorunur etiket). DD.MM.YYYY seciliyor: RangePicker'in kendi `format`'iyla
 * ayni, locale'e bagli ay adi kullanmadigi icin dayjs locale ayarlanmamis
 * bir baglamda bile dogru basar.
 */
export function donemAraligiMetni(d: Donem): string {
  const bas = dayjs(d.bas).format('DD.MM.YYYY');
  if (d.tekGun) return bas;
  const bit = dayjs(d.bit).format('DD.MM.YYYY');
  return `${bas} – ${bit}`;
}


const GUN = 'YYYY-MM-DD';

/** `g`nin bir yil onceki ayni gunu; ay yoksa (29 Subat) ayin son gunune kirpilir. */
function birYilOnce(g: Dayjs): Dayjs {
  const ay = dayjs(new Date(g.year() - 1, g.month(), 1));
  return ay.date(Math.min(g.date(), ay.daysInMonth()));
}

/**
 * K10 — onceki donem TAKVIME HIZALI. Ekranin TEK kaynagi: kartlarin Δ'si, egriler, uyarilar; 990040'in
 * ikinci cagrisi ve 990051/990055'e giden `@ONCEKIBAS`/`@ONCEKIBIT` hep bu pencereyi kullanir.
 * - gunluk / dun / hafta: gecen haftanin ayni gun(ler)i (iki uc 7 gun geri)
 * - aylik: onceki ayin 1'i .. onceki ayda ayni gun numarasi; ayin son gunune kirpilir (31 Mar -> 28/29 Sub)
 * - yillik: gecen yilin ayni gunleri (29 Sub -> 28 Sub). Veri 06.02.2026'da basliyor: onceki toplam yoksa/0
 *   ise panel Δ yerine "—" basar (OzetSerit `Rozet`, uyarilar kiyas yapmaz).
 * - ozel: hemen onceki ESIT uzunlukta pencere (990012 tanimi: uzunluk = bit - bas + 1).
 */
export function oncekiDonemHesapla(d: Pick<Donem, 'bas' | 'bit'>, tur: SatisDonemi): { bas: string; bit: string } {
  const bas = dayjs(d.bas);
  const bit = dayjs(d.bit);
  switch (tur) {
    case 'gunluk':
    case 'dun':
    case 'hafta':
      return { bas: bas.subtract(7, 'day').format(GUN), bit: bit.subtract(7, 'day').format(GUN) };
    case 'aylik': {
      // new Date(yil, -1, 1) onceki yilin Araligi'dir.
      const oncekiAy = dayjs(new Date(bas.year(), bas.month() - 1, 1));
      return {
        bas: oncekiAy.format(GUN),
        bit: oncekiAy.date(Math.min(bit.date(), oncekiAy.daysInMonth())).format(GUN),
      };
    }
    case 'yillik':
      return { bas: birYilOnce(bas).format(GUN), bit: birYilOnce(bit).format(GUN) };
    case 'ozel':
    default: {
      const uzunlukGun = bit.diff(bas, 'day') + 1;
      const oncekiBit = bas.subtract(1, 'day');
      const oncekiBas = oncekiBit.subtract(uzunlukGun - 1, 'day');
      return { bas: oncekiBas.format(GUN), bit: oncekiBit.format(GUN) };
    }
  }
}
